import ShimmerMultiColumnNominal from '@atoms/Shimmers/ShimmerMultiColumnNominal';
import ShimmerSingleColumnNominal from '@atoms/Shimmers/ShimmerSingleColumnNominal';
import TextCurrency from '@atoms/TextCurrency';
import { Text, View } from '@components/primitives';
import appStyles from '@root/appStyles';
import colors from '@theme/colors';
import React from 'react';
import { FlatList, TouchableOpacity } from 'react-native';
import { logNominalsPress, logRequestBP } from './analyticsNominals';
import styles from './styles';
import { unavailableToast } from '../BPUtils';

type NominalItem = {
  code: string;
  name: string;
  availability: boolean;
  nominal?: {
    value: number | null;
    amount: number | null;
  };
};

type ShowListOfNominalsParams = {
  noOfColumns: number;
  nominalsData: NominalItem[];
  productApiData: {
    prefix: string;
  };
  setNominalSelected: (item: NominalItem) => void;
  selectedAccountNumber: string | null;
  selectedBPAccount: {
    id: number;
  };
  requestBP: (req: {
    accountNumber: string;
    employeeAccountNumber: number;
    transCategoryPrefix: string;
    productCode: string;
    referralCode: string | null;
    zoneId: null;
    month: null;
  }) => void;
  validatedReferralCode: string | null;
  t: (key: string) => string;
  setAccountInputError: (val: string) => void;
  isLoading: boolean;
  toast: any;
  productConfig: {
    transaction_category_id: string;
    reason?: string;
    reasonId?: string;
  };
  source: string;
  meAmplitudeContext: any;
  amplitudeAccountName: string;
  isFavourite: any;
};

function getBackgroundColorForAvailableItem(item: NominalItem): string {
  return item.availability ? colors.neutral.cotton : colors.neutral.mist;
}

function getNameStyleForAvailableitem(item: NominalItem): string {
  return item.availability ? colors.neutral.charcoal : colors.disabled.smoke;
}

function getCurrencyStylesForAvailableItem(item: NominalItem): string {
  return item.availability ? colors.success.cactus : colors.disabled.smoke;
}

function getValueFromNominal(item: NominalItem): number {
  return item?.nominal?.value === null ? 0 : item?.nominal?.value ?? 0;
}

function getAmountFromNominal(item: NominalItem): number {
  return item?.nominal?.amount === null ? 0 : item?.nominal?.amount ?? 0;
}

export default function showListOfNominals(params: ShowListOfNominalsParams): React.JSX.Element {
  const {
    noOfColumns,
    nominalsData,
    productApiData,
    setNominalSelected,
    selectedAccountNumber,
    selectedBPAccount,
    requestBP,
    validatedReferralCode,
    t,
    setAccountInputError,
    isLoading,
    toast,
    productConfig,
    source,
    meAmplitudeContext,
    amplitudeAccountName,
    isFavourite,
  } = params;

  function onPressNominal(item: NominalItem): void | null {
    logNominalsPress(selectedAccountNumber || '', item, productConfig, source, validatedReferralCode, isFavourite);
    if (item.availability) {
      if (selectedAccountNumber) {
        setNominalSelected(item);
        logRequestBP(
          selectedAccountNumber,
          productConfig,
          item,
          source,
          validatedReferralCode,
          meAmplitudeContext,
          amplitudeAccountName,
          isFavourite,
        );
        requestBP({
          accountNumber: selectedAccountNumber,
          employeeAccountNumber: selectedBPAccount.id,
          transCategoryPrefix: productApiData.prefix,
          productCode: item.code,
          referralCode: validatedReferralCode,
          zoneId: null,
          month: null,
        });
      } else {
        setAccountInputError(t('bp.selectOrInputAccountNumber'));
      }
    } else {
      return unavailableToast(toast, t);
    }
    return null;
  }

  if (noOfColumns > 1) {
    if (isLoading) return <ShimmerMultiColumnNominal />;

    return (
      <FlatList
        testID="fltLstMultiCol"
        key={`multiCol${noOfColumns}`}
        data={nominalsData}
        numColumns={noOfColumns}
        style={appStyles.flex1}
        columnWrapperStyle={styles.columnWrapperStyle}
        renderItem={({ item }) => (
          <View
            style={[
              styles.nominalMultiColContainer,
              styles.nominalMultiColContainerWidth,
              { backgroundColor: getBackgroundColorForAvailableItem(item) },
            ]}>
            <TouchableOpacity
              testID={item.code}
              style={styles.nominalMultiColParent}
              onPress={() => onPressNominal(item)}>
              <TextCurrency
                color={getNameStyleForAvailableitem(item)}
                amountCurrency={getValueFromNominal(item)}
                showCurrencySymbol={false}
                variant="md-normal"
              />
              <Text variant="md-bold" color={getCurrencyStylesForAvailableItem(item)}>
                {t('bp.price')}:{' '}
                <TextCurrency
                  amountCurrency={getAmountFromNominal(item)}
                  showCurrencySymbol
                  variant="sm-bold"
                  color={getCurrencyStylesForAvailableItem(item)}
                />
              </Text>
            </TouchableOpacity>
          </View>
        )}
      />
    );
  }

  if (isLoading) return <ShimmerSingleColumnNominal />;

  return (
    <FlatList
      testID="fltLstSingleCol"
      key={`singleCol${noOfColumns}`}
      data={nominalsData}
      numColumns={noOfColumns}
      style={appStyles.flex1}
      contentContainerStyle={appStyles.marginTop(12)}
      renderItem={({ item }) => (
        <View testID={item.code} style={styles.nominalContainer}>
          <TouchableOpacity style={styles.nominalSingleCol} onPress={() => onPressNominal(item)}>
            <Text variant="md-normal" color={getNameStyleForAvailableitem(item)}>
              {item.name}
            </Text>
            <Text variant="md-bold" color={getCurrencyStylesForAvailableItem(item)} style={appStyles.marginTop(2)}>
              {t('bp.price')}:{' '}
              <TextCurrency
                amountCurrency={getAmountFromNominal(item)}
                showCurrencySymbol
                variant="sm-bold"
                color={getCurrencyStylesForAvailableItem(item)}
              />
            </Text>
          </TouchableOpacity>
        </View>
      )}
    />
  );
}
